import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSearch, 
  FaFilter, 
  FaTh, 
  FaList, 
  FaDesktop,
  FaExpand,
  FaCogs,
  FaEye,
  FaSignal,
  FaTv,
  FaChevronDown,
  FaInfoCircle,
  FaLayerGroup,
  FaRocket,
  FaClock,
  FaShieldAlt,
  FaBullhorn,
  FaStore
} from 'react-icons/fa';
import useScrollAnimation from '../../hooks/useScrollAnimation';
// Importation du hook de langue
import { useLanguage } from '../../contexts/LanguageContext';

const DigitalSignage = () => {
  // Initialisation du context de langue
  const { t } = useLanguage();

  // Liste des avantages dynamisée
  const DIGITAL_SIGNAGE_ADVANTAGES = [
    {
      id: 'custom-solutions',
      name: t.digitalSignage2.advantages.custom.name,
      description: t.digitalSignage2.advantages.custom.desc,
      icon: <FaCogs />
    },
    {
      id: 'embedded-computing',
      name: t.digitalSignage2.advantages.power.name,
      description: t.digitalSignage2.advantages.power.desc,
      icon: <FaRocket />
    },
    {
      id: 'protective-features',
      name: t.digitalSignage2.advantages.protection.name,
      description: t.digitalSignage2.advantages.protection.desc,
      icon: <FaShieldAlt />
    },
    {
      id: 'partner-network',
      name: t.digitalSignage2.advantages.partner.name,
      description: t.digitalSignage2.advantages.partner.desc,
      icon: <FaStore />
    }
  ];

  // Données des solutions dynamisées
  const DIGITAL_SIGNAGE_DATA = {
    categories: [
      {
        id: 'message-essential',
        name: t.digitalSignage2.categories.essential,
        description: 'Série ME pour signalétique numérique professionnelle',
        icon: FaBullhorn,
        color: '#dc2626'
      },
      {
        id: 'high-brightness',
        name: t.digitalSignage2.categories.brightness,
        description: 'Écrans haute luminosité pour environnements éclairés',
        icon: FaExpand,
        color: '#f59e0b'
      },
      {
        id: 'modular-computing',
        name: t.digitalSignage2.categories.modular,
        description: 'Solutions avec computing embarqué et modulaire',
        icon: FaCogs,
        color: '#059669'
      }
    ],
    
    signageProducts: [
      {
        id: 1,
        name: "Sharp MultiSync® ME752",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me752,
        keyUse: t.digitalSignage2.products.useRetail,
        options: t.digitalSignage2.products.tech_me752
      },
      {
        id: 2,
        name: "Sharp MultiSync® ME982",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me982,
        keyUse: t.digitalSignage2.products.usePublic,
        options: t.digitalSignage2.products.tech_me982
      },
      {
        id: 3,
        name: "Sharp MultiSync® ME862",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me862,
        keyUse: t.digitalSignage2.products.useHospitality,
        options: t.digitalSignage2.products.tech_me862
      },
      {
        id: 4,
        name: "Sharp MultiSync® ME652",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me652,
        keyUse: t.digitalSignage2.products.useOffice,
        options: t.digitalSignage2.products.tech_me652
      },
      {
        id: 5,
        name: "Sharp MultiSync® ME552",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me552,
        keyUse: t.digitalSignage2.products.usePoint,
        options: t.digitalSignage2.products.tech_me552
      },
      {
        id: 6,
        name: "Sharp MultiSync® ME502",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me502,
        keyUse: t.digitalSignage2.products.useBoutique,
        options: t.digitalSignage2.products.tech_me502
      },
      {
        id: 7,
        name: "Sharp MultiSync® ME432",
        image: "/images/MursEcrans/sharp-me752.jpg",
        category: "message-essential",
        description: t.digitalSignage2.products.me432,
        keyUse: t.digitalSignage2.products.useBasic,
        options: t.digitalSignage2.products.tech_me432
      }
    ]
  };

  const [filteredProducts, setFilteredProducts] = useState(DIGITAL_SIGNAGE_DATA.signageProducts);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const productsPerPage = 6;

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  // Mise à jour des produits filtrés quand la langue ou les filtres changent
  useEffect(() => {
    let filtered = [...DIGITAL_SIGNAGE_DATA.signageProducts];

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.keyUse.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy, t]); // 't' ajouté pour réagir au changement de langue

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const getCategoryInfo = (categoryId) => {
    return DIGITAL_SIGNAGE_DATA.categories.find(cat => cat.id === categoryId);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Section Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/datacenter.webp')`,
        padding: '6rem 0 4rem 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(220, 38, 38, 0.2), rgba(220, 38, 38, 0.1))',
          filter: 'blur(40px)'
        }} />
        
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="text-center text-lg-start">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Badge 
                  bg="danger" 
                  className="mb-3"
                  style={{
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}
                >
                  <FaBullhorn className="me-2" />
                  {t.digitalSignage2.hero.badge}
                </Badge>
                
                <h1 
                  style={{
                    fontSize: '3.5rem',
                    fontWeight: 700,
                    color: '#fff',
                    marginBottom: '1.5rem',
                    lineHeight: '1.2'
                  }}
                  dangerouslySetInnerHTML={{ __html: t.digitalSignage2.hero.title }}
                />
                
                <p style={{
                  fontSize: '1.3rem',
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '2rem',
                  lineHeight: '1.6',
                  maxWidth: '600px'
                }}>
                  {t.digitalSignage2.hero.subtitle}
                </p>
                
                <div className="d-flex flex-wrap gap-3">
                  <Button 
                    variant="danger" 
                    size="lg"
                    onClick={() => document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(220, 38, 38, 0.4)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaEye className="me-2" />
                    {t.digitalSignage2.hero.btnExplore}
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="lg"
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px'
                    }}
                  >
                    <FaInfoCircle className="me-2" />
                    {t.digitalSignage2.hero.btnGuide}
                  </Button>
                </div>
              </motion.div>
            </Col>
            
            <Col lg={4} className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '20px',
                  padding: '2rem',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <img 
                  src="/images/MursEcrans/SIGNAGEImage.jpg" 
                  alt="Signage Solutions"
                  style={{
                    width: '300px',
                    height: '180px',
                    objectFit: 'cover',
                    marginBottom: '1rem',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
                  }}
                />
                <h4 style={{ color: '#fff', marginBottom: '1rem' }}>
                  {t.digitalSignage2.hero.cardTitle}
                </h4>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                  {t.digitalSignage2.hero.cardSub}
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section principale avec filtres et produits */}
      <section id="solutions-section" className="py-5" style={{ backgroundColor: '#f8fafc' }}>
        <Container>
          <Row>
            {/* Sidebar avec filtres */}
            <Col lg={3}>
              <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                position: 'sticky',
                top: '2rem',
                marginBottom: '2rem'
              }}>
                {/* Recherche */}
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    <FaSearch className="me-2" />
                    {t.digitalSignage2.sidebar.search}
                  </label>
                  <Form.Control
                    type="text"
                    placeholder={t.digitalSignage2.sidebar.searchPl}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e2e8f0',
                      fontSize: '0.9rem',
                      padding: '0.75rem 1rem'
                    }}
                  />
                </div>

                {/* Filtre par catégorie */}
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    <FaFilter className="me-2" />
                    {t.digitalSignage2.sidebar.category}
                  </label>
                  
                  <div className="d-grid gap-2">
                    <Button
                      variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'}
                      onClick={() => handleCategoryChange('all')}
                      size="sm"
                      style={{ 
                        borderRadius: '10px', 
                        fontSize: '0.8rem',
                        padding: '0.5rem 0.75rem',
                        textAlign: 'left'
                      }}
                    >
                      {t.digitalSignage2.sidebar.all}
                    </Button>
                    {DIGITAL_SIGNAGE_DATA.categories.map(category => (
                      <Button
                        key={category.id}
                        variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'}
                        onClick={() => handleCategoryChange(category.id)}
                        size="sm"
                        style={{ 
                          borderRadius: '10px', 
                          fontSize: '0.8rem',
                          padding: '0.5rem 0.75rem',
                          textAlign: 'left'
                        }}
                      >
                        <category.icon className="me-2" /> {category.name}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Options d'affichage */}
                <div className="mb-4">
                  <label style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#4a5568',
                    marginBottom: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block'
                  }}>
                    {t.digitalSignage2.sidebar.display}
                  </label>
                  <div className="d-flex gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'}
                      onClick={() => setViewMode('grid')}
                      size="sm"
                      style={{ borderRadius: '10px', flex: 1 }}
                    >
                      <FaTh />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'danger' : 'outline-secondary'}
                      onClick={() => setViewMode('list')}
                      size="sm"
                      style={{ borderRadius: '10px', flex: 1 }}
                    >
                      <FaList />
                    </Button>
                  </div>
                </div>

                {/* Résultats */}
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                  borderRadius: '12px',
                  border: '1px solid #dee2e6',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ 
                    color: '#495057', 
                    margin: 0,
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textAlign: 'center'
                  }}>
                    <strong style={{ color: '#dc2626' }}>{filteredProducts.length}</strong> {t.digitalSignage2.sidebar.found}
                  </p>
                  {totalPages > 1 && (
                    <p style={{ 
                      color: '#6c757d', 
                      margin: '0.25rem 0 0 0',
                      fontSize: '0.75rem',
                      textAlign: 'center'
                    }}>
                      Page {currentPage} {t.digitalSignage2.sidebar.pageOf} {totalPages}
                    </p>
                  )}
                </div>
              </div>
            </Col>

            {/* Zone principale des produits */}
            <Col lg={9}>
              {viewMode === 'grid' ? (
                <Row className="g-4">
                  {paginatedProducts.map((product, index) => (
                    <Col md={6} xl={4} key={product.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        whileHover={{ y: -5 }}
                        style={{ height: '100%' }}
                      >
                        <Card style={{
                          height: '100%',
                          borderRadius: '20px',
                          border: 'none',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{
                            height: '200px',
                            background: `linear-gradient(135deg, ${getCategoryInfo(product.category)?.color}20, ${getCategoryInfo(product.category)?.color}40)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}>
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                            />
                            <Badge
                              style={{
                                position: 'absolute',
                                top: '1rem',
                                right: '1rem',
                                backgroundColor: getCategoryInfo(product.category)?.color,
                                borderRadius: '25px',
                                padding: '0.5rem 1rem',
                                fontSize: '0.75rem'
                              }}
                            >
                              {getCategoryInfo(product.category)?.name}
                            </Badge>
                          </div>

                          <Card.Body style={{ padding: '1.5rem' }}>
                            <h5 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem', fontSize: '1.1rem' }}>
                              {product.name}
                            </h5>

                            <p style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: '1rem' }}>
                              {product.description}
                            </p>

                            {/* Usage clé */}
                            <div className="mb-3">
                              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0' }}>
                                <div className="text-center">
                                  <div style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                                    {t.digitalSignage2.card.usage}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>
                                    {product.keyUse}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Technologies */}
                            <div className="mb-3">
                              <h6 style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
                                <FaCogs size={12} color="#dc2626" className="me-2" />
                                {t.digitalSignage2.card.tech}
                              </h6>
                              <div style={{ background: '#f1f5f9', borderRadius: '8px', padding: '0.75rem', border: '1px solid #e2e8f0' }}>
                                {product.options.split(',').map((option, idx) => (
                                  <div key={idx} style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center', marginBottom: '0.2rem' }}>
                                    <span style={{ width: '4px', height: '4px', backgroundColor: '#dc2626', borderRadius: '50%', marginRight: '0.5rem' }}></span>
                                    <span>{option.trim()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="d-grid gap-4">
                  {paginatedProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card style={{ borderRadius: '20px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <Row className="g-0">
                          <Col md={4}>
                            <div style={{
                              height: '250px',
                              background: `linear-gradient(135deg, ${getCategoryInfo(product.category)?.color}20, ${getCategoryInfo(product.category)?.color}40)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
                            }}>
                              <img src={product.image} alt={product.name} style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
                              <Badge style={{ position: 'absolute', top: '1rem', left: '1rem', backgroundColor: getCategoryInfo(product.category)?.color, borderRadius: '25px', padding: '0.5rem 1rem' }}>
                                {getCategoryInfo(product.category)?.name}
                              </Badge>
                            </div>
                          </Col>
                          <Col md={8}>
                            <Card.Body style={{ padding: '2rem' }}>
                              <h4 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '1rem' }}>{product.name}</h4>
                              <p style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>{product.description}</p>
                              <Row>
                                <Col sm={6}>
                                  <div className="mb-3">
                                    <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>{t.digitalSignage2.card.usage}</small>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{product.keyUse}</div>
                                  </div>
                                </Col>
                                <Col sm={6}>
                                  <div className="mb-3">
                                    <small style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>{t.digitalSignage2.card.tech}</small>
                                    <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '0.5rem' }}>
                                      {product.options.split(',').map((option, idx) => (
                                        <div key={idx} style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center' }}>
                                          <span style={{ width: '3px', height: '3px', backgroundColor: '#dc2626', marginRight: '0.4rem' }}></span>
                                          <span>{option.trim()}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Col>
                        </Row>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <div className="d-flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Button
                        key={i + 1}
                        variant={currentPage === i + 1 ? "danger" : "outline-danger"}
                        onClick={() => setCurrentPage(i + 1)}
                        style={{ borderRadius: '50%', width: '40px', height: '40px', fontWeight: 600 }}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message si aucun résultat */}
              {filteredProducts.length === 0 && (
                <div className="text-center py-5">
                  <FaBullhorn size={64} color="#64748b" />
                  <h4 style={{ color: '#64748b', marginTop: '1rem' }}>
                    {t.digitalSignage2.card.noResults}
                  </h4>
                  <p style={{ color: '#64748b' }}>
                    {t.digitalSignage2.card.noResultsSub}
                  </p>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default DigitalSignage;